import { ref, computed } from 'vue';
import { createSharedComposable, tryOnBeforeMount } from '@vueuse/core';
import type { LsPlgUsage } from '@eureka/services/src/apis/common.api.types';
import { controlDeferred, type IControl } from '@eureka/utils/control';

import { StripeApi } from '@eureka/services/src/apis/stripe.api';
import type { ICreatePaymentSessionRequest } from '@eureka/services/src/apis/stripe.api.types';
import { combineHash } from '@eureka/utils/url';
import type { UsageItem } from '../types';
import { PRICE_MAP } from '../constants';
import type { Account } from '@patsnap-utils/biz';
import { accountDeferred } from '@eureka/utils/account';

/**
 * LS 版本用量管理 - 整合业务逻辑和 API 调用
 */
export const useLsEditionUsage = createSharedComposable(() => {
    const lsPlgUsage = ref<LsPlgUsage | null>(null);
    const isLoading = ref(false);
    const error = ref<Error | null>(null);

    const controlInfo = ref<IControl | null>(null);
    const account = ref<Account | null>(null);

    tryOnBeforeMount(async() => {
        controlInfo.value = await controlDeferred.promise;
        account.value = await accountDeferred.promise;
    })

    /**
     * 计算进度百分比
     */
    function getProgressPercentage(current: number = 0, max: number = 0): number {
        if (!max || max === 0) return 0;
        return Math.min(Math.round((current / max) * 100), 100);
    }

    /**
     * Projects 用量（MB）
     */
    const projects = computed<UsageItem | null>(() => {
        const usage = lsPlgUsage.value?.usage.find(i => i.key === 'project_storage')
        if (!usage || !usage.support) return null

        return {
            current: usage.current_amount,
            available: usage.available_amount,
            max: usage.max_amount,
            progress: getProgressPercentage(usage.current_amount, usage.max_amount),
        };
    });

    // ========== API 调用 ==========

    async function createPaymentSession(params: ICreatePaymentSessionRequest) {
        const res = await StripeApi.createPaymentSession(params);
        return res;
    }

    /**
     * 升级逻辑 - 跳转到 LS 套餐页面
     */
    const handleUpgrade = () => {
        location.hash = combineHash('', 'user-setting/package/ls/plg');
    };

    /**
     * 检查用户是否已有包含 PRICE_MAP 中任意 priceId 的有效订阅
     * @returns Promise<boolean> - true: 已有有效订阅, false: 没有有效订阅
     */
    async function checkHasActiveSubscription(): Promise<boolean> {
        const res = await StripeApi.getSubscriptionList();

        // 从 PRICE_MAP 中提取所有的 priceId(所有版本)
        const allPriceIds: string[] = [];

        Object.values(PRICE_MAP).forEach(p => {
            Object.values(p).forEach(priceList => {
                priceList.forEach(priceItem => {
                    allPriceIds.push(priceItem.priceId);
                });
            });
        });

        // 筛选激活状态且包含 PRICE_MAP 中任意 priceId 的订阅
        const activeSubscriptions = res.filter(subscription => {
            // 必须是激活状态
            if (subscription.subscription_status !== 'active') return false;

            // items 中必须包含 PRICE_MAP 中的任意 price_id
            const hasValidPrice = subscription.items.some(item =>
                allPriceIds.includes(item.price_id)
            );

            return hasValidPrice;
        });

        return activeSubscriptions.length > 0;
    }

    return {
        // 数据状态
        isLoading,
        error,

        // Usage 数据
        projects,

        // 工具函数
        handleUpgrade,
        createPaymentSession,
        checkHasActiveSubscription,
    };
});
